import React, { useEffect, useMemo } from 'react';
import FlagProvider, { IConfig, UnleashClient, useUnleashContext } from '@unleash/proxy-client-react';

import { useAuthStore } from '@/modules/user/stores';

import { buildUnleashUserId } from './utils/buildUnleashUserId';
import { FeatureFlagContextProvider } from './FeatureFlagContext';

const getUnleashConfig = (): IConfig | null => {
  const url = window.REACT_APP_UNLEASH_API_URL || import.meta.env.REACT_APP_UNLEASH_API_URL;
  const clientKey = window.REACT_APP_UNLEASH_API_TOKEN || import.meta.env.REACT_APP_UNLEASH_API_TOKEN;
  const appName = 'admin-fe';

  if (!url || !clientKey) {
    return null;
  }

  return {
    url,
    clientKey,
    appName,
    // Set to 0 means flags will be fetched only on login or page refresh
    refreshInterval: 0,
  };
};

// Create client outside of React to prevent duplicate initialization in StrictMode
const config = getUnleashConfig();
const unleashClient = config ? new UnleashClient(config) : null;

interface FeatureFlagContextUpdaterProps {
  children: React.ReactNode;
}

const FeatureFlagContextUpdater: React.FC<FeatureFlagContextUpdaterProps> = ({ children }) => {
  const { user } = useAuthStore();
  const updateContext = useUnleashContext();

  const unleashUserId = useMemo(
    () => (user?.contactId ? buildUnleashUserId(user.contactId) : undefined),
    [user?.contactId]
  );

  useEffect(() => {
    if (unleashUserId) {
      updateContext({
        userId: unleashUserId,
        properties: {
          organizationId: user?.organizationId || '',
          organizationPath: user?.organizationPath || '',
          username: user?.username || '',
        },
      });
    }
  }, [unleashUserId, user?.organizationId, user?.organizationPath, user?.username, updateContext]);

  return <>{children}</>;
};

interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  if (!unleashClient) {
    return <FeatureFlagContextProvider isConfigured={false}>{children}</FeatureFlagContextProvider>;
  }

  return (
    <FlagProvider unleashClient={unleashClient} startClient>
      <FeatureFlagContextProvider isConfigured>
        <FeatureFlagContextUpdater>{children}</FeatureFlagContextUpdater>
      </FeatureFlagContextProvider>
    </FlagProvider>
  );
};
